import React from 'react';
import { Tier } from '../types/game';
import { PlayerAchievement } from '../types/achievements';
import { achievementDefinitions } from '../data/achievements';

interface GameResultProps {
  gameOver: boolean; // true = hearts ran out, false = completed
  score: number;
  totalQuestions: number;
  questionsAnswered: number;
  correctAnswers: number;
  heartsRemaining: number;
  maxStreak: number;
  userTier: Tier;
  onPlayAgain: () => void;
  onUpgrade: (tier: 'PREMIUM' | 'VIP') => void;
  sessionAchievements: string[];
}

const GameResult: React.FC<GameResultProps> = ({
  gameOver,
  score,
  totalQuestions,
  questionsAnswered,
  correctAnswers,
  heartsRemaining,
  maxStreak,
  userTier,
  onPlayAgain,
  onUpgrade,
  sessionAchievements,
}) => {
  const accuracy = questionsAnswered > 0 ? Math.round((correctAnswers / questionsAnswered) * 100) : 0;

  return (
    <div className="trivia-result game-result-panel">
      <h2>{gameOver ? 'Game Over!' : 'Game Complete!'}</h2>
      {gameOver && <p className="game-result-subtitle">You ran out of hearts!</p>}

      <div className="result-stats-grid">
        <div className="result-stat">
          <span className="result-stat-value">{score.toLocaleString()}</span>
          <span className="result-stat-label">Total Points</span>
        </div>
        <div className="result-stat">
          <span className="result-stat-value">{correctAnswers}/{questionsAnswered}</span>
          <span className="result-stat-label">Correct</span>
        </div>
        <div className="result-stat">
          <span className="result-stat-value">{accuracy}%</span>
          <span className="result-stat-label">Accuracy</span>
        </div>
        <div className="result-stat">
          <span className="result-stat-value">{maxStreak}</span>
          <span className="result-stat-label">Max Streak</span>
        </div>
        <div className="result-stat">
          <span className="result-stat-value">{'❤️'.repeat(heartsRemaining)}</span>
          <span className="result-stat-label">Hearts Left</span>
        </div>
      </div>

      {sessionAchievements.length > 0 && (
        <div className="result-achievements">
          <h3>Achievements Unlocked</h3>
          <div className="result-achievements-list">
            {sessionAchievements.map((id) => {
              const def = achievementDefinitions.find((a) => a.id === id);
              if (!def) return null;
              return (
                <div key={id} className={`result-achievement-badge rarity-${def.rarity.toLowerCase()}`}>
                  <span className="achievement-icon">{def.icon}</span>
                  <span className="achievement-name">{def.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <button onClick={onPlayAgain} className="play-again-btn shimmer-animation">Play Again</button>

      {userTier === 'FREE' && (
        <div className="upgrade-prompt">
          <p>Unlock more questions &amp; power-ups!</p>
          <button onClick={() => onUpgrade('PREMIUM')} className="upgrade-btn">Premium ($5)</button>
          <button onClick={() => onUpgrade('VIP')} className="upgrade-btn vip">VIP ($15)</button>
        </div>
      )}
    </div>
  );
};

export default GameResult;
